import { useState, useMemo, useCallback } from 'react';
import { useRecipeSearch as useRecipeSearchBase, useRandomRecipes } from './useRecipes';
import { RecipeSearchParams, Recipe } from '@/types/api';

interface UseRecipeSearchState {
  filters: RecipeSearchParams;
  results: Recipe[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasNextPage: boolean;
  totalCount: number;
  currentPage: number;
}

interface UseRecipeSearchActions {
  updateFilters: (newFilters: Partial<RecipeSearchParams>) => void;
  clearFilters: () => void;
  nextPage: () => void;
  prevPage: () => void;
  setPage: (page: number) => void;
  refetch: () => void;
}

interface UseRecipeSearchOptions {
  initialFilters?: Partial<RecipeSearchParams>;
  defaultLimit?: number;
  enableRandomFallback?: boolean;
}

export function useRecipeSearch(options: UseRecipeSearchOptions = {}) {
  const {
    initialFilters = {},
    defaultLimit = 12,
    enableRandomFallback = true
  } = options;

  // Search state
  const [filters, setFilters] = useState<RecipeSearchParams>({
    query: '',
    page: 1,
    limit: defaultLimit,
    ...initialFilters
  });

  // Compute whether we should use random recipes vs search
  const shouldUseRandom = useMemo(() => {
    return enableRandomFallback && 
           !filters.query && 
           !filters.category && 
           !filters.area && 
           !filters.ingredient && 
           !filters.maxTime;
  }, [filters, enableRandomFallback]);

  // Use appropriate queries based on filters
  const searchQuery = useRecipeSearchBase(filters);
  // Run an ingredient-based query in parallel when there is a text query.
  // This enables fallback like "pechuga de pollo" -> ingredient search.
  const ingredientFilters: RecipeSearchParams = useMemo(() => ({
    ...filters,
    // If we already have an explicit ingredient filter, keep it.
    // Otherwise, try using the text query as ingredient.
    ingredient: filters.ingredient || (filters.query ? filters.query : undefined),
    // Avoid mixing name and ingredient simultaneously to keep backend behavior predictable
    query: filters.ingredient || (filters.query ? undefined : filters.query),
  }), [filters]);
  const ingredientQuery = useRecipeSearchBase(ingredientFilters);

  const randomQuery = useRandomRecipes(filters.limit || defaultLimit, { 
    enabled: shouldUseRandom,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Decide whether to fall back to ingredient results when name search is empty
  const useIngredientFallback = useMemo(() => {
    if (shouldUseRandom) return false;
    if (!filters.query) return false;
    const totalByName = searchQuery.data?.total || 0;
    return totalByName === 0;
  }, [shouldUseRandom, filters.query, searchQuery.data?.total]);

  // Select active query
  const activeQuery = shouldUseRandom
    ? randomQuery
    : (useIngredientFallback ? ingredientQuery : searchQuery);

  // Extract results
  const results = useMemo(() => {
    if (shouldUseRandom) {
      return randomQuery.data || [];
    }
    if (useIngredientFallback) {
      return ingredientQuery.data?.recipes || [];
    }
    return searchQuery.data?.recipes || [];
  }, [shouldUseRandom, randomQuery.data, useIngredientFallback, ingredientQuery.data, searchQuery.data]);

  // Extract metadata
  const totalCount = useMemo(() => {
    if (shouldUseRandom) {
      return results.length;
    }
    if (useIngredientFallback) {
      return ingredientQuery.data?.total || 0;
    }
    return searchQuery.data?.total || 0;
  }, [shouldUseRandom, results.length, useIngredientFallback, ingredientQuery.data?.total, searchQuery.data?.total]);

  const hasNextPage = useMemo(() => {
    if (shouldUseRandom) {
      return false;
    }
    const data = searchQuery.data;
    return data ? data.page < data.totalPages : false;
  }, [shouldUseRandom, searchQuery.data]);

  // Actions
  const updateFilters = useCallback((newFilters: Partial<RecipeSearchParams>) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      // Reset to first page when filters change (except when explicitly setting page)
      page: 'page' in newFilters ? newFilters.page! : 1
    }));
  }, []); // No dependencies needed - uses setFilters callback form

  const clearFilters = useCallback(() => {
    setFilters({
      query: '',
      page: 1,
      limit: defaultLimit
    });
  }, [defaultLimit]);

  const nextPage = useCallback(() => {
    if (hasNextPage) {
      setFilters(prev => ({ ...prev, page: (prev.page || 1) + 1 }));
    }
  }, [hasNextPage]);

  const prevPage = useCallback(() => {
    setFilters(prev => ({ 
      ...prev, 
      page: Math.max(1, (prev.page || 1) - 1) 
    }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters(prev => ({ ...prev, page: Math.max(1, page) }));
  }, []);

  const refetch = useCallback(() => {
    if (shouldUseRandom) {
      randomQuery.refetch();
    } else {
      searchQuery.refetch();
    }
  }, [shouldUseRandom, randomQuery.refetch, searchQuery.refetch]);

  // State object
  const state: UseRecipeSearchState = {
    filters,
    results,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    error: activeQuery.error,
    hasNextPage,
    totalCount,
    currentPage: filters.page || 1
  };

  // Actions object
  const actions: UseRecipeSearchActions = {
    updateFilters,
    clearFilters,
    nextPage,
    prevPage,
    setPage,
    refetch
  };

  return {
    ...state,
    ...actions,
    // Additional computed properties
    hasPrevPage: (filters.page || 1) > 1,
    isFirstPage: (filters.page || 1) === 1,
    totalPages: shouldUseRandom ? 1 : Math.ceil(totalCount / (filters.limit || defaultLimit)),
    isEmpty: !activeQuery.isLoading && results.length === 0,
    isRandomMode: shouldUseRandom
  };
}

export type UseRecipeSearchReturn = ReturnType<typeof useRecipeSearch>;
